/**
 * Slim client for tideway-kai. Extracted from the production
 * `frontend/src/services/api.ts` KaiApiClient — covers conversation listing,
 * creation, retrieval, and SSE streaming. No changes to the Kai server.
 */

import type { FeedSeverity, ScopeDimension, TimeHorizon } from "@/types/feed";

type TokenGetter = () => Promise<string>;

const KAI_BASE_URL =
  import.meta.env.VITE_KAI_URL || "http://localhost:3001/api/v1/kai";

export type KaiChatRole = "user" | "assistant" | "system" | "tool";

export interface KaiChatMessage {
  id: string;
  conversation_id: string;
  role: KaiChatRole;
  content: string | null;
  tool_name?: string | null;
  created_at: string;
  seq: number;
  metadata?: {
    plan_execute?: {
      plan?: {
        title?: string;
        steps: { id: string; label: string; status?: string }[];
      };
    };
    attachment_ids?: string[];
    filenames?: string[];
    artifact_ids?: { kind: string; id: string }[];
    /** `@` tags from the composer, persisted on user messages. */
    entity_tags?: KaiEntityTagPayload[];
    /** `/` slash command from the composer, persisted on user messages. */
    slash_command?: { id: string; label: string };
  } | null;
}

/** A deterministically-built structured artifact (e.g. a `rate_comparison` card). */
export interface KaiArtifactResponse {
  id: string;
  kind: string;
  payload: unknown;
  created_at: string;
}

export interface KaiConversationSummary {
  id: string;
  title: string | null;
  model: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface KaiConversationWithMessages {
  id: string;
  client_key: string;
  user_id: string;
  title: string | null;
  model: string;
  created_at: string;
  updated_at: string;
  messages: KaiChatMessage[];
}

export interface KaiTableContext {
  page: "bookings" | "recommendations";
  visible_columns: string[];
  hidden_columns: string[];
  column_order: string[];
  page_size: number;
  content_density: "comfortable" | "compact";
  sticky_columns?: { first?: number; last?: number };
  group_by?: string;
}

/** A compact snapshot of one Home widget for Kai's layout context. */
export interface KaiHomeWidgetSummary {
  widget_key: string;
  widget_type: string;
  title: string;
}

/** Snapshot of the user's current Home dashboard, sent while on `/dashboard`. */
export interface KaiHomeContext {
  widgets: KaiHomeWidgetSummary[];
  kai_session_active: boolean;
}

export interface KaiFilterContext {
  page: "bookings";
  operation: "and" | "or";
  active_tokens: { property_key: string; operator: string; value: string }[];
}

/** Snapshot of the active Activity Feed scope, sent while on `/feed`. */
export interface KaiFeedContext {
  scope_dimension?: ScopeDimension;
  scope_key?: string;
  time_horizon?: TimeHorizon;
  severity?: FeedSeverity;
  event_type?: string;
}

/**
 * A single feed event the user asked Kai to explain (via "Ask Kai" on a feed
 * row). Seeds a fresh chat so Kai fetches the full event with
 * `api_get_feed_event` and keeps it in scope for follow-ups.
 */
export interface KaiFeedEventContext {
  event_id: string;
  correlation_id: string;
  event_type: string;
  title: string;
  severity?: string;
  reference_type?: string | null;
  reference_id?: string | null;
  children_count?: number;
  /**
   * Graph mail linkage, present only when the selected feed event is a
   * classified Outlook message (`kind: "graph_mail"`). Lets Kai open the exact
   * message with `get_graph_mail_context` instead of re-searching.
   */
  graph_message_id?: string | null;
  mailbox_id?: string | null;
  conversation_id?: string | null;
  booking_source_id?: string | null;
  /**
   * Document attachments on the mail event, so Kai can resolve "download the
   * PDF" against the exact file without re-fetching the message. When there is
   * exactly one, it becomes the active attachment in session scope.
   */
  graph_attachments?: Array<{
    mailbox_id: string;
    message_id: string;
    attachment_id: string;
    filename: string;
  }>;
}

/**
 * The booking the user is currently viewing (route `/bookings/:sourceId`).
 * Seeds the turn so Kai treats this booking as the active one for BL /
 * document / pricing follow-ups without the user pasting the id.
 */
export interface KaiBookingDetailContext {
  source_id: string;
}

/**
 * A single email the user drilled into from a `mail_review_snapshot` card
 * (row action). Carries the Graph linkage so Kai opens the exact message with
 * `get_graph_mail_context` instead of re-searching, and keeps it in scope for
 * follow-ups.
 */
export interface KaiMailReviewContext {
  /** Stored `clients.graph_messages.id` for the selected message. */
  message_id: string;
  mailbox_id?: string | null;
  /** Graph conversation key for `get_graph_mail_thread`. */
  conversation_id?: string | null;
  booking_source_id?: string | null;
  subject?: string | null;
  from_address?: string | null;
}

/** An entity the user explicitly tagged with `@` for the current message. */
export interface KaiEntityTagPayload {
  kind:
    | "user"
    | "sales_person"
    | "agent"
    | "consignee"
    | "shipper"
    | "carrier"
    | "port"
    | "contract_ref"
    | "booking_state";
  value: string;
  label: string;
  metadata?: {
    role?: string;
    sales_person_link?: string | null;
    opened_by_values?: string[];
    /** `port` tag: origin / destination / either. */
    port_role?: "origin" | "destination" | "either";
    port_summary?: string;
    /** `contract_ref` tag: owning carrier + rate family. */
    carrier?: string;
    source_type?: "collect" | "prepaid" | "spot" | "lcl";
    named_account?: string;
    state_filter_mode?: "state" | "state_not";
    ops_hint?: string;
    booking_state_id?: string;
  };
}

export interface KaiClientContext {
  route?: string;
  language?: string;
  role?: string;
  sales_person_link?: string;
  table_context?: KaiTableContext;
  filter_context?: KaiFilterContext;
  home_context?: KaiHomeContext;
  feed_context?: KaiFeedContext;
  feed_event_context?: KaiFeedEventContext;
  booking_detail_context?: KaiBookingDetailContext;
  mail_review_context?: KaiMailReviewContext;
  entity_tags?: KaiEntityTagPayload[];
  slash_command?: KaiSlashCommandPayload;
}

export interface KaiSseEvent {
  type: string;
  data: string;
}

/** A file uploaded to a Kai conversation, with its extraction outcome. */
export interface KaiAttachment {
  id: string;
  filename: string;
  content_type: string;
  file_size: number;
  extraction_status: "pending" | "processing" | "done" | "failed";
  document_kind: string | null;
  table_count: number;
  preview_text: string | null;
  extraction_error: string | null;
}

export interface CachedEntitySummary {
  summary: string;
  cached_at: string;
}

/** A single data-backed welcome-screen starter prompt. */
export interface KaiWelcomeSuggestion {
  id: string;
  text: string;
}

export interface KaiWelcomeSuggestionsResponse {
  prompts: KaiWelcomeSuggestion[];
  generated_at: string;
  source: string;
}

/** A built-in `/` slash command available to the caller (already RBAC-filtered). */
export interface KaiSlashCommand {
  id: string;
  label: string;
  hint: string;
  category: string;
  placeholder: string;
}

export interface KaiSlashCommandsResponse {
  commands: KaiSlashCommand[];
}

/** The `/` slash command the user invoked for a message. */
export interface KaiSlashCommandPayload {
  id: string;
  args?: string;
}

class KaiApiClient {
  private baseUrl: string;
  private getToken: TokenGetter | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setTokenGetter(getter: TokenGetter) {
    this.getToken = getter;
  }

  private async authHeader(): Promise<Record<string, string>> {
    if (!this.getToken) return {};
    // Let token-acquisition errors propagate: the token getter redirects to
    // login on silent-auth failure, so we must not fire a token-less request.
    const token = await this.getToken();
    return { Authorization: `Bearer ${token}` };
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(await this.authHeader()),
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ message: `HTTP ${response.status}` }));
      const error = new Error(body.message || `HTTP ${response.status}`);
      (error as Error & { status: number }).status = response.status;
      throw error;
    }

    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  async listConversations(
    limit = 50,
    offset = 0,
  ): Promise<KaiConversationSummary[]> {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    return this.request<KaiConversationSummary[]>(`/conversations?${params}`);
  }

  async createConversation(title?: string): Promise<KaiConversationWithMessages> {
    return this.request<KaiConversationWithMessages>("/conversations", {
      method: "POST",
      body: JSON.stringify({ title }),
    });
  }

  async getConversation(id: string): Promise<KaiConversationWithMessages> {
    return this.request<KaiConversationWithMessages>(
      `/conversations/${encodeURIComponent(id)}`,
    );
  }

  async submitFeedback(
    conversationId: string,
    messageId: string,
    sentiment: "helpful" | "not_helpful",
    comment?: string,
  ): Promise<unknown> {
    return this.request<unknown>(
      `/conversations/${encodeURIComponent(conversationId)}/feedback`,
      {
        method: "POST",
        body: JSON.stringify({ message_id: messageId, sentiment, comment }),
      },
    );
  }

  /** Fetch a single stored artifact for a conversation (tenant-scoped). */
  async getArtifact(
    conversationId: string,
    artifactId: string,
  ): Promise<KaiArtifactResponse> {
    return this.request<KaiArtifactResponse>(
      `/conversations/${encodeURIComponent(
        conversationId,
      )}/artifacts/${encodeURIComponent(artifactId)}`,
    );
  }

  /** List every stored artifact for a conversation (for prefetch on load). */
  async listArtifacts(conversationId: string): Promise<KaiArtifactResponse[]> {
    return this.request<KaiArtifactResponse[]>(
      `/conversations/${encodeURIComponent(conversationId)}/artifacts`,
    );
  }

  /**
   * Rebuild a `rate_card_onboarding` artifact's payload from its stored
   * `builder_input`, deterministically (no LLM). Returns the refreshed artifact.
   */
  async refreshArtifact(
    conversationId: string,
    artifactId: string,
  ): Promise<KaiArtifactResponse> {
    return this.request<KaiArtifactResponse>(
      `/conversations/${encodeURIComponent(
        conversationId,
      )}/artifacts/${encodeURIComponent(artifactId)}/refresh`,
      { method: "POST" },
    );
  }

  async respondToClarification(
    clarificationId: string,
    answers: Record<string, string>,
  ): Promise<{ status: string }> {
    return this.request<{ status: string }>(
      `/clarifications/${encodeURIComponent(clarificationId)}/respond`,
      {
        method: "POST",
        body: JSON.stringify({ answers }),
      },
    );
  }

  /**
   * Approve or reject a pending MCP elicitation (e.g. rate-card creation).
   * The agent's tool call is blocked server-side until this resolves.
   */
  async respondToElicitation(
    elicitationId: string,
    action: "accept" | "decline" | "cancel",
    content?: Record<string, unknown>,
  ): Promise<{ status: string }> {
    return this.request<{ status: string }>(
      `/elicitations/${encodeURIComponent(elicitationId)}/respond`,
      {
        method: "POST",
        body: JSON.stringify({ action, content }),
      },
    );
  }

  /**
   * Send a message and consume the SSE response. `onEvent` receives each parsed
   * event as `{ type, data }`, where `data` is the raw JSON string payload.
   */
  async streamMessage(
    conversationId: string,
    content: string,
    onEvent: (event: KaiSseEvent) => void,
    signal?: AbortSignal,
    attachmentIds?: string[],
    clientContext?: KaiClientContext,
  ): Promise<void> {
    const url = `${this.baseUrl}/conversations/${encodeURIComponent(
      conversationId,
    )}/messages/stream`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(await this.authHeader()),
      },
      body: JSON.stringify({
        content,
        attachment_ids: attachmentIds ?? [],
        client_context: { language: "en", ...(clientContext ?? {}) },
      }),
      signal,
    });

    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ message: "Stream request failed" }));
      throw new Error(body.message || `HTTP ${response.status}`);
    }

    await consumeSseStream(response, onEvent);
  }

  /**
   * Upload one or more files to a conversation. The server runs document
   * extraction (PDF/OCR/table parsing) before returning each attachment's
   * outcome. Do NOT set `Content-Type`: the browser adds the multipart
   * boundary.
   */
  async uploadAttachments(
    conversationId: string,
    files: File[],
    options?: { signal?: AbortSignal },
  ): Promise<KaiAttachment[]> {
    const url = `${this.baseUrl}/conversations/${encodeURIComponent(
      conversationId,
    )}/attachments`;

    const form = new FormData();
    for (const file of files) {
      form.append("files", file, file.name);
    }

    const response = await fetch(url, {
      method: "POST",
      headers: { ...(await this.authHeader()) },
      body: form,
      signal: options?.signal,
    });

    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ message: "Upload failed" }));
      throw new Error(body.message || `HTTP ${response.status}`);
    }

    return (await response.json()) as KaiAttachment[];
  }

  async deleteAttachment(
    conversationId: string,
    attachmentId: string,
  ): Promise<void> {
    await this.request<void>(
      `/conversations/${encodeURIComponent(
        conversationId,
      )}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: "DELETE" },
    );
  }

  /**
   * Fetch the raw bytes of an uploaded attachment as an object URL. Callers are
   * responsible for revoking the returned URL. Used to render image thumbnails
   * in sent messages.
   */
  async getAttachmentRawUrl(
    conversationId: string,
    attachmentId: string,
  ): Promise<string> {
    const url = `${this.baseUrl}/conversations/${encodeURIComponent(
      conversationId,
    )}/attachments/${encodeURIComponent(attachmentId)}/raw`;

    const response = await fetch(url, {
      headers: { ...(await this.authHeader()) },
    });
    if (!response.ok) {
      throw new Error(`Failed to load source file (HTTP ${response.status})`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  /**
   * Cached Kai entity summary (daily briefing, booking insight, …). Returns
   * `null` on a cache miss or when insights are disabled for the org.
   */
  async getCachedEntitySummary(
    entityType: string,
    entityId: string,
  ): Promise<CachedEntitySummary | null> {
    try {
      return await this.request<CachedEntitySummary | null>(
        `/entity-summary/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
      );
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      if (status === 403) return null;
      throw err;
    }
  }

  /**
   * Data-backed, role-scoped welcome-screen starter prompts. Returns an empty
   * list when the `kai_welcome_prompts` flag is off (403) or when generation
   * yields nothing, so callers fall back to role-based curated prompts.
   */
  async getWelcomeSuggestions(): Promise<KaiWelcomeSuggestion[]> {
    try {
      const res = await this.request<KaiWelcomeSuggestionsResponse>(
        "/welcome-suggestions",
      );
      return res.prompts ?? [];
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      if (status === 403) return [];
      throw err;
    }
  }

  /**
   * Built-in `/` slash commands available to the caller, already filtered by
   * role, permission, savings visibility, and (optionally) the current route.
   * The frontend fetches this once per session to populate the composer menu.
   */
  async getSlashCommands(route?: string): Promise<KaiSlashCommand[]> {
    const params = new URLSearchParams();
    if (route) params.set("route", route);
    const query = params.toString();
    const res = await this.request<KaiSlashCommandsResponse>(
      `/slash-commands${query ? `?${query}` : ""}`,
    );
    return res.commands ?? [];
  }

  /**
   * Generate a fresh entity summary and stream SSE frames (`text_delta`,
   * `tool_start`, `done`, …). Always overwrites the cached row on completion.
   */
  async streamEntitySummary(
    entityType: string,
    entityId: string,
    onEvent: (event: KaiSseEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const url = `${this.baseUrl}/entity-summary/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/stream`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": "en",
        Accept: "text/event-stream",
        ...(await this.authHeader()),
      },
      signal,
    });

    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ message: "Stream request failed" }));
      const error = new Error(body.message || `HTTP ${response.status}`);
      (error as Error & { status: number }).status = response.status;
      throw error;
    }

    await consumeSseStream(response, onEvent);
  }
}

/** Parse an SSE response, keeping event/data state across TCP chunk boundaries. */
async function consumeSseStream(
  response: Response,
  onEvent: (event: KaiSseEvent) => void,
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body for SSE stream");

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";
  let currentData = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("event:")) {
        currentEvent = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        currentData = line.slice(5).trim();
      } else if (line === "" && currentEvent && currentData) {
        onEvent({ type: currentEvent, data: currentData });
        currentEvent = "";
        currentData = "";
      }
    }
  }
}

export const kaiApi = new KaiApiClient(KAI_BASE_URL);
